import { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Text,
  UnorderedList,
  ListItem,
  Divider,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import ProcessingCard from 'components/ProcessingCard';

import ValidationsSummary from 'views/validated_policy/components/ValidationsSummary';

import { apiValidations } from 'api';

const ValidatedPolicy = ({
  customAuth,
  policyUuid,
  submissionId,
  onValidationResults,
}: {
  customAuth?: CustomAuth;
  policyUuid?: string;
  submissionId?: number | null;
  onValidationResults?: () => void;
}) => {
  const { uuid: urlUuid } = useParams();
  const uuid = policyUuid || urlUuid;
  const [isMvrPending, setIsMvrPending] = useState(false);
  const [hadMvrPending, setHadMvrPending] = useState(false);
  const [mvrEstimatedWait, setMvrEstimatedWait] = useState<number | null>(null);
  const [mvrNextAvailableAt, setMvrNextAvailableAt] = useState<string | null>(
    null,
  );
  const [mvrServiceUnavailable, setMvrServiceUnavailable] = useState(false);
  const [mvrAvailabilityMessage, setMvrAvailabilityMessage] = useState<
    string | null
  >(null);
  const [currentStage, setCurrentStage] = useState<string | null>(null);
  const [stages, setStages] = useState<Record<
    string,
    { status: string; label: string }
  > | null>(null);

  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['validations', uuid],
    queryFn: async () => {
      const response = await apiValidations(uuid, customAuth, submissionId);

      if (response && response.status === 202) {
        const cgiMvrStatus = response.data?.cgi_mvr_status;
        const processingStages = response.data?.processing_stages;

        if (processingStages) {
          setCurrentStage(processingStages.current_stage);
          setStages(processingStages.stages);
        }

        if (cgiMvrStatus?.service_unavailable) {
          setMvrServiceUnavailable(true);
          setMvrAvailabilityMessage(cgiMvrStatus.availability_message ?? null);
        } else if (cgiMvrStatus?.pending) {
          setIsMvrPending(true);
          setHadMvrPending(true);
          setMvrEstimatedWait(cgiMvrStatus.estimated_wait ?? null);
          setMvrNextAvailableAt(cgiMvrStatus.next_available_at ?? null);
        } else {
          setIsMvrPending(false);
        }
        throw {
          status: 202,
          message: 'Processing in progress',
          cgi_mvr_status: cgiMvrStatus,
        };
      }

      // Processing complete, clear transient states
      setIsMvrPending(false);
      setHadMvrPending(false);
      setMvrEstimatedWait(null);
      setMvrNextAvailableAt(null);
      setCurrentStage(null);
      setStages(null);

      // Check for service unavailable in final response (persists after refresh)
      // Note: 200 response returns data directly, not wrapped in { data: ... }
      const finalCgiStatus = response.cgi_mvr_status;
      if (finalCgiStatus?.service_unavailable) {
        setMvrServiceUnavailable(true);
        setMvrAvailabilityMessage(finalCgiStatus.availability_message ?? null);
      }

      if (onValidationResults) onValidationResults();

      return response;
    },
    retry: (failureCount, error) => {
      if (error?.status === 202) {
        return true;
      }

      if (error?.status === 400) {
        return false;
      }

      return failureCount < 3;
    },
    retryDelay: (attemptIndex, error) => {
      if (error?.status === 202) {
        const cgiMvrStatus = error?.cgi_mvr_status;
        if (cgiMvrStatus?.pending && cgiMvrStatus?.polling_interval) {
          return cgiMvrStatus.polling_interval * 1000;
        }

        return 3000;
      }

      return Math.min(1000 * 2 ** attemptIndex, 30000);
    },
  }) as {
    data: {
      validations: Validation[];
    };
    isLoading: boolean;
    error: any;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleRedirect = () => {
    navigate(`/admin/policies/submission/${submissionId}/${uuid}/personal`);
    window.scrollTo(0, 0);
  };

  const validations = data?.validations || [];

  const getCanSubmitApp = () => {
    if (!submissionId) return false;
    return true;
  };

  if (error) {
    return (
      <Card>
        <Text fontWeight="bold" fontSize="2xl" mb="20px">
          Errors detected while parsing files
        </Text>

        <Alert status="error">
          <AlertIcon />
          <AlertDescription>
            <UnorderedList>
              {error.error.map((err: any, index: number) => {
                return (
                  <div key={index}>
                    {Object.entries(err).map(([key, value], i) => (
                      <ListItem key={i}>
                        <strong style={{ textTransform: 'capitalize' }}>
                          {key}
                        </strong>{' '}
                        - {value as string}
                      </ListItem>
                    ))}
                  </div>
                );
              })}
            </UnorderedList>
          </AlertDescription>
        </Alert>

        <Text mt="20px">Reference ID: {uuid}</Text>
      </Card>
    );
  }

  const formatMinutes = (seconds: number) => {
    const minutes = Math.round(seconds / 60);
    if (minutes <= 1) return '1 minute';
    return `${minutes} minutes`;
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getMvrWaitTimeMessage = () => {
    if (mvrNextAvailableAt) {
      return `Results expected around ${formatTime(mvrNextAvailableAt)}.`;
    }

    if (mvrEstimatedWait) {
      return `Estimated wait: ~${formatMinutes(mvrEstimatedWait)}.`;
    }

    return 'This may take a few minutes.';
  };

  const getProcessingTitle = () => {
    // Phase 1: Initial processing (no stages yet)
    if (!stages || !currentStage) {
      return 'Checking Policy Info Against Underwriting Guidelines...';
    }

    // Non-immediate province (AB) - show phase-specific titles
    if (hadMvrPending) {
      if (isMvrPending) {
        // Phase 2: MVR pending
        return 'Fetching Driver Records...';
      }
      if (currentStage === 'validations') {
        // Phase 3: MVR complete, running validations
        return 'Running Final Validations...';
      }
    }

    // Immediate province (ON, NB, NS) - simple message
    return 'Checking Policy Info Against Underwriting Guidelines...';
  };

  const getDisplayStages = (): Record<
    string,
    { status: 'pending' | 'in_progress' | 'complete' | 'failed'; label: string }
  > | null => {
    // Only show stages for non-immediate provinces (when we've seen MVR pending)
    if (!hadMvrPending || !stages) return null;

    const mapStatus = (
      status: string,
    ): 'pending' | 'in_progress' | 'complete' | 'failed' => {
      if (status === 'complete' || status === 'skipped') {
        return 'complete';
      }
      if (status === 'in_progress') {
        return 'in_progress';
      }
      if (status === 'failed' || status === 'partial') {
        return 'failed';
      }
      return 'pending';
    };

    const getDashLabel = () => {
      const status = stages.dash?.status;
      if (status === 'complete') return 'DASH reports fetched';
      if (status === 'skipped') return 'DASH reports skipped';
      if (status === 'failed') return 'DASH reports failed';
      if (status === 'partial') return 'DASH reports partially fetched';
      return 'Fetching DASH reports...';
    };

    // Display stages: DASH, MVR, Validations
    return {
      dash: {
        status: mapStatus(stages.dash?.status || 'pending'),
        label: getDashLabel(),
      },
      mvr: {
        status: mapStatus(stages.mvr?.status || 'pending'),
        label:
          stages.mvr?.status === 'complete'
            ? 'Driver records fetched'
            : 'Fetching driver records from CGI...',
      },
      validations: {
        status: mapStatus(stages.validations?.status || 'pending'),
        label:
          stages.validations?.status === 'in_progress'
            ? 'Running validations...'
            : 'Running validations',
      },
    };
  };

  const getSubtitle = () => {
    // Only show wait time when MVR is currently pending
    if (!isMvrPending) return undefined;
    return getMvrWaitTimeMessage();
  };

  if (isLoading || validations.length === 0) {
    return (
      <ProcessingCard
        title={getProcessingTitle()}
        stages={getDisplayStages()}
        subtitle={getSubtitle()}
      />
    );
  }

  return (
    <>
      {mvrServiceUnavailable && (
        <Alert status="info" mb={4} borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Driver records could not be fetched.
            {mvrAvailabilityMessage && ` ${mvrAvailabilityMessage}`}
          </AlertDescription>
        </Alert>
      )}
      <ValidationsSummary
        validations={validations}
        footer={
          getCanSubmitApp() && (
            <>
              <Divider mt="20px" />
              <Button
                mt="20px"
                alignSelf="center"
                fontSize={{ base: 'md', lg: 'lg' }}
                variant="green"
                fontWeight="500"
                w="100%"
                h={{ base: '45px', md: '50px' }}
                onClick={handleRedirect}
              >
                Autofill Application and Start Submission
              </Button>
            </>
          )
        }
      />
    </>
  );
};

export default ValidatedPolicy;
